import { type Editor, generateText, type JSONContent } from '@tiptap/core'
import { findThreadsInDocument } from '@tiptap-pro/extension-comments'
import type { TiptapCollabProvider } from '@tiptap-pro/provider'
import type { ThreadCommentData, ThreadData } from '../hooks/useThreads'
import type { EditorThread } from '../types'

/**
 * Flattens a comment's ProseMirror JSON to plain text,
 * rendering nodes(mentions included) via their extensions.
 */
const commentText = (
  editor: Editor,
  content: JSONContent | undefined
): string =>
  content
    ? generateText(content, editor.extensionManager.extensions, {
        blockSeparator: '\n',
      })
    : ''

/**
 * Maps each thread id to the document text it's anchored to, gathered from every
 * node carrying that id (inline comment marks and block-level threads).
 */
const anchoredTextByThread = (editor: Editor): Map<string, string> => {
  const anchors = new Map<string, string>()
  for (const { thread, node } of findThreadsInDocument(editor, {})) {
    const text = node.isText ? (node.text ?? '') : node.textContent
    anchors.set(thread, `${anchors.get(thread) ?? ''}${text}`)
  }
  return anchors
}

/**
 * Reads a collaboration document's comment threads into a neutral shape.
 * Returns ONLY unresolved unarchived threads.
 */
export const getUnresolvedThreads = (
  editor: Editor,
  provider: TiptapCollabProvider | null
): EditorThread[] => {
  if (!provider) return []

  const anchors = anchoredTextByThread(editor)

  return (
    provider
      // Only return unresolved unarchived threads
      .getThreads<ThreadData, ThreadCommentData>({ types: ['unarchived'] })
      .filter((thread) => thread.resolvedAt == null && thread.deletedAt == null)
      .map((thread) => ({
        anchoredText: (anchors.get(thread.id) ?? '').trim(),
        comments: thread.comments.map((comment) => ({
          author: comment.data?.userName ?? '',
          text: commentText(editor, comment.content),
        })),
      }))
  )
}
